import React from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { DAY_MONTH, MONTH_DAY } from '../../../../../constants';
import { useRootSelector } from '../../../../../store/hooks';
import { selectSiteHeaderData } from '../../../../../store/features/siteHeader';
import { renderHTMLString } from '../../../../../helpers';
import Modal from '../../../../shared/Modal/Modal';
import { withBrand } from '../../../../../providers/BrandProvider';
import { FormBoxInput } from '../../../../shared/forms';
import VatMessage from '../../../VatMessage/VatMessage';
import Link from '../../../../shared/Link/Link';
import PaymentFormBlock from '../../PaymentFormBlock/PaymentFormBlock';
import ProductPopup from './ProductPopup/ProductPopup';
import OptionList from './OptionList/OptionList';

import './ProductItem.scss';
import { selectPageConfig } from '../../../../../store/features/pageConfig';

export function getTierFormattedDate(date, dateFormat = DAY_MONTH) {
  return dateFormat === MONTH_DAY ? date.split(' ').reverse().join(' ') : date;
}

export function ProductItem(props) {
  const { t } = useTranslation();
  const { dateFormat = DAY_MONTH } = useRootSelector(selectSiteHeaderData);
  const { location } = useRootSelector(selectPageConfig);

  const {
    productType: { productTypeId, selectionType } = {},
    productItem: {
      id: productItemId,
      name: productItemName,
      vat,
      price,
      priceInclVat,
      tieredSaving,
      description,
      productOptionsList = [],
    } = {
      productOptionsList: [],
    },
    tier: { endDate: tierEndDate } = {},
    selected = {
      primarySelected: false,
      optionsList: {},
    },
    disabled = false,
    vatRate = 0,
    taxLabelStyle = 'VAT',
    isPriceInclVat = false,
    onProductChange,
    onOptionChange,
    brand: { secondaryColor } = {},
  } = props;
  const ProductPopupLink = Modal(Link, ProductPopup);

  return (
    selectionType && (
      <div
        className={`c-product-item${
          selected.disabled || disabled ? ' disabled' : ''
        }`}
      >
        <PaymentFormBlock checked={selected.selected} showLeftLine>
          <div className="row">
            <div className="col-xs-12">
              <FormBoxInput
                type={selectionType}
                value={`${productTypeId}:${productItemId}`}
                checked={selected.selected}
                disabled={selected.disabled || disabled}
                onChange={onProductChange}
                noCheckbox={selected.primarySelected}
              >
                <div className="row">
                  <div className="col-xs-12 col-sm-7">
                    <div
                      className={`c-product-item__content${
                        description ? ' has-description' : ''
                      }${vatRate > 0 ? ' has-vat-rate' : ''}${
                        productItemId === 'none' ? ' none-item' : ''
                      }`}
                    >
                      <div className="c-product-item__label">
                        {productItemName}
                      </div>
                      {description && description.length > 130 ? (
                        <ProductPopupLink
                          content={{
                            heading: productItemName,
                            body: renderHTMLString(description, location),
                          }}
                          className="c-product-item__more-info"
                          style={{ color: secondaryColor }}
                        >
                          {t('event.purchase.more-information')}
                        </ProductPopupLink>
                      ) : (
                        description && (
                          <div className="c-product-item__description">
                            {renderHTMLString(description, location)}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  <div className="col-xs-12 col-sm-5">
                    <div className="c-product-item__price">
                      {isPriceInclVat ? priceInclVat : price}
                    </div>
                    {vat && vatRate > 0 && (
                      <div className="c-product-item__vat-amount">
                        <VatMessage
                          isPriceInclVat={isPriceInclVat}
                          vat={vat}
                          vatRate={vatRate}
                          taxLabelStyle={taxLabelStyle}
                          shortMessage
                          className="c-vat-message--mobile-left-aligned"
                        />
                      </div>
                    )}
                    {productOptionsList.length > 0 && !selected.selected && (
                      <div className="c-product-item__option-flag">
                        {t('event.purchase.choose.product.option.available')}
                        <i className="icon fa fa-chevron-down" />
                      </div>
                    )}
                  </div>
                </div>
              </FormBoxInput>
            </div>
            {productOptionsList.length > 0 && selected.selected && (
              <div className="col-xs-12">
                <div
                  className={`c-product-item__option-list${
                    selected.primarySelected
                      ? ' c-product-item__option-list--full'
                      : ''
                  }`}
                >
                  {productOptionsList.map((optionList, index) => (
                    <OptionList
                      key={index}
                      listIndex={index}
                      selected={selected.optionsList[index]}
                      productType={props.productType}
                      productItem={props.productItem}
                      onOptionChange={onOptionChange}
                    />
                  ))}
                </div>
              </div>
            )}
            {tieredSaving && tierEndDate && (
              <div className="col-xs-12">
                <div
                  className={`c-product-item__tiered-saving${
                    selected.primarySelected
                      ? ' c-product-item__tiered-saving--full'
                      : ''
                  }`}
                >
                  <Trans
                    i18nKey="event.purchase.save-by-date"
                    values={{
                      0: tieredSaving,
                      1: getTierFormattedDate(tierEndDate, dateFormat),
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </PaymentFormBlock>
      </div>
    )
  );
}

ProductItem.propTypes = {
  productType: PropTypes.object,
  productItem: PropTypes.object,
  selected: PropTypes.object,
  disabled: PropTypes.bool,
  tier: PropTypes.object,
  vatRate: PropTypes.number,
  taxLabelStyle: PropTypes.string,
  isPriceInclVat: PropTypes.bool,
  onProductChange: PropTypes.func,
  onOptionChange: PropTypes.func,
  brand: PropTypes.object,
};

export default withBrand(ProductItem);
